import React, { useMemo, useRef, useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { Form, Field } from 'react-final-form';
import PhoneInput from 'react-phone-number-input';
import { Button, Captcha, NamedLink } from '../../../components';
import {
  composeValidators,
  required,
  accepted,
  emailFormatValid,
  phoneFormatValid,
} from '../../../util/validators';
import css from './SectionContactUs.module.css';
import './react-phone-number-input.css';
import { t, tJsx } from '../../../util/messages';
import { submitContactForm } from '../../../util/api';
import {
  configContactServices,
  configContactBudgets,
} from '../../../config/configContactForm';

const SectionContactUs = ({ rootClassName, className }) => {
  const reCaptchaRef = useRef(null);

  /**
   * Timestamp used as a bot heuristic: the API rejects submissions that
   * arrive faster than a person could plausibly complete the form. useMemo
   * (not useState) because it must never trigger a re-render.
   */
  const formRenderedAt = useMemo(() => Date.now(), []);
  const classes = classNames(rootClassName || css.root, className);

  const [contactState, setContactState] = useState({
    inProgress: false,
    error: null,
    success: false,
  });
  const [captchaError, setCaptchaError] = useState(null);

  // ── Validators ────────────────────────────────────────────────────────────
  const nameRequired = required(t('SectionContactUs.nameRequired'));

  const isValidEmail = composeValidators(
    required(t('SectionContactUs.emailRequired')),
    emailFormatValid(t('SectionContactUs.emailValid'))
  );

  const isValidPhone = composeValidators(
    required(t('SectionContactUs.phoneRequired')),
    phoneFormatValid(t('SectionContactUs.phoneValid'))
  );

  const serviceRequired = required(t('SectionContactUs.serviceRequiredMessage'));
  const budgetRequired = required(t('SectionContactUs.budgetRequiredMessage'));
  const messageRequired = required(t('SectionContactUs.messageRequiredMessage'));
  /*
   * `accepted`, not `required`: consent is only given by a tick. See the
   * Field below for why this box used to pass validation while unticked.
   */
  const privacyPolicyAccepted = accepted(t('SectionContactUs.privacyPolicyRequiredMessage'));

  /*
   * Options come from config, which documents that both lists are validated
   * against enums in the API. Previously they were hard-coded here with
   * nothing indicating that adding one without a matching server change
   * would make those submissions fail.
   */
  const serviceOptions = configContactServices.map(service => ({
    value: service.id,
    label: t(service.label),
  }));

  const budgetOptions = configContactBudgets;

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = async values => {
    let token;
    try {
      token = await reCaptchaRef.current?.execute('contact_form');
    } catch {
      setCaptchaError(t('SectionContactUs.captchaRequired'));
      return;
    }

    if (!token) {
      setCaptchaError(t('SectionContactUs.captchaRequired'));
      return;
    }
    setCaptchaError(null);
    setContactState({ inProgress: true, error: null, success: false });

    try {
      await submitContactForm({
        name: values.name,
        phone: values.phonenumber,
        email: values.email,
        companyName: values.companyName || '',
        description: values.message,
        service: values.service,
        budget: values.budget,
        // `=== true`, never `!!`: `!![]` is true, which is how an unticked
        // box used to satisfy the API's `privacyPolicy !== true` guard.
        privacyPolicy: values.privacyPolicy === true,
        recaptchaToken: token,
        // Anti-bot fields — the API discards both.
        website: values.website || '',
        formRenderedAt,
      });
      setContactState({ inProgress: false, error: null, success: true });
    } catch (error) {
      // ApiError already carries a message that is safe to show a visitor.
      setContactState({
        inProgress: false,
        error: error?.message || t('SectionContactUs.errorContacting'),
        success: false,
      });
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section id="contact-us" className={classes} aria-label="Contact Us">
      <div className={css.content}>
        <header className={css.wrapper} data-reveal>
          <div className={css.left}>
            <span className={css.tag}>
              <i aria-hidden="true" />
              {t('SectionContactUs.eyebrow')}
            </span>
            <h2 className={css.title}>{t('SectionContactUs.heading')}</h2>
          </div>
          <p className={css.description}>{t('SectionContactUs.description')}</p>
        </header>

        <div className={css.grid}>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting, pristine }) => (
            <form onSubmit={handleSubmit} className={css.form} noValidate>

              {/* Name */}
              <div className={css.row}>
                <Field name="name" validate={nameRequired}>
                  {({ input, meta }) => (
                    <div>
                      <label htmlFor="contact-name">{t('SectionContactUs.name')}</label>
                      <input
                        {...input}
                        id="contact-name"
                        type="text"
                        placeholder={t('SectionContactUs.namePlaceHolder')}
                        aria-required="true"
                        aria-describedby={meta.error && meta.touched ? 'contact-name-error' : undefined}
                      />
                      {meta.error && meta.touched && (
                        <span id="contact-name-error" role="alert" className={css.error}>
                          {meta.error}
                        </span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              {/* Email */}
              <div className={css.row}>
                <Field name="email" validate={isValidEmail}>
                  {({ input, meta }) => (
                    <div>
                      <label htmlFor="contact-email">{t('SectionContactUs.email')}</label>
                      <input
                        {...input}
                        id="contact-email"
                        type="email"
                        placeholder={t('SectionContactUs.emailPlaceHolder')}
                        aria-required="true"
                        aria-describedby={meta.error && meta.touched ? 'contact-email-error' : undefined}
                      />
                      {meta.error && meta.touched && (
                        <span id="contact-email-error" role="alert" className={css.error}>
                          {meta.error}
                        </span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              {/* Phone */}
              <div className={css.row}>
                <Field name="phonenumber" validate={isValidPhone}>
                  {({ input, meta }) => (
                    <div>
                      <label htmlFor="contact-phone">{t('SectionContactUs.phoneNumber')}</label>
                      <PhoneInput
                        {...input}
                        id="contact-phone"
                        className={css.phone}
                        placeholder={t('SectionContactUs.phoneNumberPlaceHolder')}
                        aria-required="true"
                        aria-describedby={meta.error && meta.touched ? 'contact-phone-error' : undefined}
                      />
                      {meta.error && meta.touched && (
                        <span id="contact-phone-error" role="alert" className={css.error}>
                          {meta.error}
                        </span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              {/* Company Name */}
              <div className={css.row}>
                <Field name="companyName">
                  {({ input }) => (
                    <div>
                      <label htmlFor="contact-company">{t('SectionContactUs.companyName')}</label>
                      <input
                        {...input}
                        id="contact-company"
                        type="text"
                        placeholder={t('SectionContactUs.companyNamePlaceHolder')}
                      />
                    </div>
                  )}
                </Field>
              </div>

              {/*
                Service and budget are chip groups, not dropdowns: the option
                sets are short and the choices are worth showing at a glance
                rather than hiding behind a select. Rendered as a radiogroup
                so keyboard and screen-reader users get the same semantics a
                native control would give them.
              */}
              <div className={classNames(css.row, css.chipField)}>
                {/*
                  No default. With eight options a pre-selected one mostly
                  records itself rather than the visitor's intent; the field
                  is required, so an explicit choice costs one tap and gives
                  honest lead data.
                */}
                <Field name="service" validate={serviceRequired}>
                  {({ input, meta }) => (
                    <div>
                      <span className={css.chipLabel} id="contact-service-label">
                        {t('SectionContactUs.service')}
                      </span>
                      <div className={css.chips} role="radiogroup" aria-labelledby="contact-service-label">
                        {serviceOptions.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="radio"
                            aria-checked={input.value === opt.value}
                            className={classNames(css.chip, {
                              [css.chipSelected]: input.value === opt.value,
                            })}
                            onClick={() => input.onChange(opt.value)}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {meta.error && meta.touched && (
                        <span role="alert" className={css.error}>{meta.error}</span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              <div className={classNames(css.row, css.chipField)}>
                <Field name="budget" validate={budgetRequired}>
                  {({ input, meta }) => (
                    <div>
                      <span className={css.chipLabel} id="contact-budget-label">
                        {t('SectionContactUs.budget')}
                      </span>
                      <div className={css.chips} role="radiogroup" aria-labelledby="contact-budget-label">
                        {budgetOptions.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            role="radio"
                            aria-checked={input.value === opt}
                            className={classNames(css.chip, {
                              [css.chipSelected]: input.value === opt,
                            })}
                            onClick={() => input.onChange(opt)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      {meta.error && meta.touched && (
                        <span role="alert" className={css.error}>{meta.error}</span>
                      )}
                    </div>
                  )}
                </Field>
              </div>

              {/* Message */}
              <div className={classNames(css.row, css.message)}>
                <Field name="message" validate={messageRequired}>
                  {({ input, meta }) => (
                    <div>
                      <label htmlFor="contact-message">{t('SectionContactUs.messageLabel')}</label>
                      <textarea
                        {...input}
                        id="contact-message"
                        placeholder={t('SectionContactUs.messagePlaceHolder')}
                        aria-required="true"
                        aria-describedby={meta.error && meta.touched ? 'contact-message-error' : undefined}
                      />
                      {meta.error && meta.touched && (
                        <span id="contact-message-error" role="alert" className={css.error}>
                          {meta.error}
                        </span>
                      )}
                      {/*
                        Replaces the "Send me an NDA" checkbox. The offer is
                        worth making — it is the moment someone hesitates
                        about what to type — but a checkbox created a tracked
                        obligation and sat next to the required consent box,
                        where a second tick reads as more consent.
                      */}
                      <p className={css.fieldHint}>{t('SectionContactUs.ndaHint')}</p>
                    </div>
                  )}
                </Field>
              </div>

              {/*
                Honeypot. Positioned off-screen, excluded from the tab order
                and hidden from assistive tech, so only automation fills it.
                autoComplete="off" stops browsers populating it helpfully.
              */}
              <div className={css.honeypot} aria-hidden="true">
                <Field
                  name="website"
                  component="input"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  id="contact-website"
                />
              </div>

              {/*
                * Privacy Policy
                *
                * No `value` prop, deliberately. With one, React Final Form
                * maintains an ARRAY for this field rather than a boolean
                * ("we are maintaining an array, not just a boolean" — its
                * own source), so unticking left `[]` instead of `false`.
                * `[]` passed `required`, `!![]` is `true`, and the API's
                * `privacyPolicy !== true` guard was handed that `true` — so
                * the form submitted with consent unticked. Without the prop
                * the value is a plain boolean, which `accepted` can judge.
                *
                * The error is rendered too: it validated before, but nothing
                * displayed `meta.error`, so a blocked submit looked like a
                * dead button.
                */}
              <Field name="privacyPolicy" type="checkbox" validate={privacyPolicyAccepted}>
                {({ input, meta }) => (
                  <div className={classNames(css.row, css.checkbox)}>
                    <input
                      {...input}
                      id="contact-privacyPolicy"
                      className={css.inlineCheckbox}
                      aria-required="true"
                      aria-describedby={
                        meta.error && meta.touched ? 'contact-privacyPolicy-error' : undefined
                      }
                    />
                    <label htmlFor="contact-privacyPolicy">
                      {/* The policy is linked, not just named: consent to a
                          document nobody can open is not informed consent.
                          It opens in a new tab so a part-filled form is not
                          lost to the navigation. */}
                      {tJsx('SectionContactUs.privacyPolicy', {
                        privacyPolicyLink: (
                          <NamedLink
                            name="PrivacyPolicyPage"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={css.inlineLink}
                          >
                            {t('SectionContactUs.privacyPolicyLinkText')}
                          </NamedLink>
                        ),
                      })}
                    </label>
                    {meta.error && meta.touched && (
                      <span id="contact-privacyPolicy-error" role="alert" className={css.error}>
                        {meta.error}
                      </span>
                    )}
                  </div>
                )}
              </Field>

              {/* reCAPTCHA Enterprise v3 — invisible, ref used to execute on submit */}
              <Captcha ref={reCaptchaRef} />
              {captchaError && (
                <div className={classNames(css.row, css.recaptchaContainer)}>
                  <span role="alert" className={css.error}>{captchaError}</span>
                </div>
              )}

              {/* Submit */}
              <div className={classNames(css.row, css.submitButtonContainer)}>
                {contactState.error && (
                  <p role="alert" className={css.error}>
                    {/* ApiError messages are already visitor-safe; the generic
                        string is only a fallback for an unexpected shape. */}
                    {typeof contactState.error === 'string'
                      ? contactState.error
                      : t('SectionContactUs.errorContacting')}
                  </p>
                )}
                {contactState.success && (
                  <p role="status" className={css.success}>
                    {t('SectionContactUs.successMessage')}
                  </p>
                )}
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={contactState.inProgress}
                  disabled={submitting || pristine}
                  ready={contactState.success}
                >
                  {t('SectionContactUs.contactBtnMessage')}
                </Button>
                {/* Required in place of the reCAPTCHA badge, which is hidden
                    in defaults.css so it does not sit under the chat bubble. */}
                <p className={css.fieldHint}>
                  {tJsx('SectionContactUs.recaptchaNotice', {
                    googlePrivacyLink: (
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.inlineLink}
                      >
                        {t('SectionContactUs.recaptchaPrivacyLinkText')}
                      </a>
                    ),
                    googleTermsLink: (
                      <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={css.inlineLink}
                      >
                        {t('SectionContactUs.recaptchaTermsLinkText')}
                      </a>
                    ),
                  })}
                </p>
              </div>
            </form>
          )}
          />

          <aside className={css.aside}>
            <div className={css.card}>
              <p className={css.cardLabel}>{t('SectionContactUs.emailLabel')}</p>
              <a className={css.cardValueLink} href="mailto:contact@gitribe.com">
                {t('Footer.gitribeEmail')}
              </a>
            </div>
            <div className={css.card}>
              <p className={css.cardLabel}>{t('SectionContactUs.phoneLabel')}</p>
              <a className={css.cardValue} href="tel:+923054000453">
                {t('Footer.phone')}
              </a>
            </div>
            <div className={css.card}>
              <p className={css.cardLabel}>{t('SectionContactUs.preferToTalk')}</p>
              <a
                className={css.cardValueLink}
                href={
                  process.env.REACT_APP_SCHEDULE_MEETING_URL ||
                  'https://calendly.com/gitribe/30min'
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Footer.scheduleMeeting')}
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

SectionContactUs.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionContactUs);
